import { useEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Sparkles, Send, Loader2 } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export const AI_ELIGIBLE = (user) =>
  user?.role === "super_admin" ||
  ["nataraju.s@united-group.in", "shabbir@united-group.in"].includes((user?.email || "").toLowerCase());

export const SUGGESTIONS = [
  "What's the total pipeline value by zone?",
  "Which Hot leads are closing in the next 60 days?",
  "Top 10 customers by billing this year",
  "Which large deals are still missing required info?",
  "Who filed the most DSR calls in the last 30 days?",
  "Summarise wins and losses with reasons",
];

function Md({ text }) {
  const lines = (text || "").split("\n");
  const bold = (s) =>
    s.split(/(\*\*[^*]+\*\*)/g).map((p, i) =>
      p.startsWith("**") && p.endsWith("**") ? <strong key={i}>{p.slice(2, -2)}</strong> : p
    );
  return (
    <div className="space-y-1">
      {lines.map((l, i) => {
        const t = l.trim();
        if (!t) return <div key={i} className="h-1.5" />;
        if (t.startsWith("###")) return <p key={i} className="font-semibold text-slate-900 mt-1">{bold(t.replace(/^#+\s*/, ""))}</p>;
        if (/^[-*•]\s/.test(t)) return <p key={i} className="pl-4 relative"><span className="absolute left-1 text-slate-400">•</span>{bold(t.replace(/^[-*•]\s/, ""))}</p>;
        if (/^\d+[.)]\s/.test(t)) return <p key={i} className="pl-4">{bold(t)}</p>;
        return <p key={i}>{bold(t)}</p>;
      })}
    </div>
  );
}

export function AIChat({ sessionId, onSession, compact = false, showSuggestions = true }) {
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const endRef = useRef(null);
  const scroll = () => endRef.current?.scrollIntoView({ behavior: "smooth" });

  const { data: dynSugg } = useQuery({
    queryKey: ["ai-suggestions"],
    queryFn: async () => (await api.get("/ai/suggestions")).data,
    enabled: showSuggestions,
    staleTime: 5 * 60 * 1000,
  });
  const suggestions = dynSugg?.suggestions?.length ? dynSugg.suggestions : SUGGESTIONS;

  useEffect(() => {
    let live = true;
    if (!sessionId) { setMessages([]); return; }
    api.get(`/ai/sessions/${sessionId}/messages`)
      .then((r) => { if (live) setMessages(r.data.map((m) => ({ role: m.role, content: m.content }))); })
      .catch(() => { if (live) { setMessages([]); onSession?.(null); } });
    return () => { live = false; };
  }, [sessionId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { scroll(); }, [messages]);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || streaming) return;
    setInput("");
    setStreaming(true);
    setMessages((m) => [...m, { role: "user", content: msg }, { role: "assistant", content: "" }]);
    try {
      const res = await fetch(`${BACKEND}/api/ai/chat`, {
        method: "POST",
        headers: { Authorization: `Bearer ${localStorage.getItem("sc_token")}`, "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionId, message: msg }),
      });
      if (!res.ok || !res.body) throw new Error(`HTTP ${res.status}`);
      const reader = res.body.getReader();
      const dec = new TextDecoder();
      let buf = "";
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += dec.decode(value, { stream: true });
        const parts = buf.split("\n\n");
        buf = parts.pop();
        for (const p of parts) {
          if (!p.startsWith("data: ")) continue;
          let j;
          try { j = JSON.parse(p.slice(6)); } catch { continue; }
          if (j.delta) setMessages((m) => { const c = [...m]; c[c.length - 1] = { ...c[c.length - 1], content: c[c.length - 1].content + j.delta }; return c; });
          if (j.error) setMessages((m) => { const c = [...m]; c[c.length - 1] = { role: "assistant", content: `⚠️ ${j.error}` }; return c; });
          if (j.done && j.session_id) onSession?.(j.session_id);
        }
      }
    } catch (e) {
      setMessages((m) => { const c = [...m]; c[c.length - 1] = { role: "assistant", content: `⚠️ Could not reach the AI service (${e.message}).` }; return c; });
    } finally {
      setStreaming(false);
    }
  };

  return (
    <div className="flex flex-col h-full min-h-0" data-testid="ai-chat">
      <div className={`flex-1 overflow-y-auto min-h-0 ${compact ? "p-3" : "p-4 sm:p-6"} space-y-4`} data-testid="ai-messages">
        {messages.length === 0 && (
          <div className="pt-4">
            <div className="flex items-center gap-2 text-slate-800 mb-1">
              <Sparkles className="h-5 w-5 text-brand" />
              <p className="font-heading font-semibold">Ask SalesCore anything</p>
            </div>
            <p className="text-sm text-slate-500 mb-4">Hi {user?.name?.split(" ")[0]} — I can answer questions across leads, pipeline, billing, targets, dealers, universe, DSRs and field events using live CRM data.</p>
            {showSuggestions && (
              <div className="flex flex-wrap gap-2">
                {suggestions.map((s) => (
                  <button key={s} onClick={() => send(s)}
                    className="text-xs text-left px-3 py-2 rounded-lg border border-slate-200 bg-white text-slate-600 hover:border-brand hover:text-brand transition-colors"
                    data-testid="ai-suggestion">
                    {s}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
        {messages.map((m, i) => (
          <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
            <div className={`max-w-[85%] rounded-xl px-3.5 py-2.5 text-sm leading-relaxed ${m.role === "user" ? "bg-slate-900 text-white rounded-br-sm" : "bg-white border border-slate-200 text-slate-700 rounded-bl-sm"}`}
              data-testid={`ai-msg-${m.role}`}>
              {m.role === "assistant" && !m.content && streaming && i === messages.length - 1
                ? <span className="flex items-center gap-2 text-slate-400"><Loader2 className="h-3.5 w-3.5 animate-spin" /> Analysing CRM data…</span>
                : m.role === "assistant" ? <Md text={m.content} /> : m.content}
            </div>
          </div>
        ))}
        <div ref={endRef} />
      </div>
      <div className={`border-t border-slate-200 bg-white ${compact ? "p-2.5" : "p-3 sm:p-4"}`}>
        <div className="flex gap-2 items-end">
          <Textarea value={input} onChange={(e) => setInput(e.target.value)} rows={compact ? 1 : 2}
            onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
            placeholder="Ask about pipeline, billing, leads, team…" className="resize-none text-sm min-h-0"
            data-testid="ai-input" />
          <Button onClick={() => send()} disabled={streaming || !input.trim()}
            className="bg-brand hover:bg-brand/90 text-white h-10 w-10 p-0 shrink-0" data-testid="ai-send">
            {streaming ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
          </Button>
        </div>
      </div>
    </div>
  );
}
